import {
  type GridRowParams,
  type GridValidRowModel,
  useGridApiRef,
} from "@mui/x-data-grid-premium";

import React, { Suspense, useState } from "react";

import { useModuleTabs } from "src/framework/layout/ModuleTabsProvider.tsx";
import { useDataRouterContext } from "src/lib/react-router/useDataRouterContext.ts";
import { useDeleteOne } from "src/system/useDeleteOne.ts";
import { DeleteRecordConfirmation } from "src/admin/modals/DeleteRecordConfirmation/DeleteRecordConfirmation.tsx";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import type { DataGridContextMenuProps } from "src/components/DataGrid/useDataGridRowContextMenu.tsx";
import { ResourceList } from "src/system/ResourceList.tsx";
import { useClientConfig } from "src/client-config/useClientConfig.ts";
import { SelectProductType } from "src/admin/modals/product/SelectProductType/SelectProductType.tsx";
import { useBoolean } from "src/hooks/use-boolean.ts";
import { AppVariation } from "src/client-config/getClientConfig.ts";
import { replacePathParams } from "@exonn/utils";
import { ResourceEnum } from "src/constants/constants.ts";
import { getProductRowPath } from "src/framework/listV2/getProductRowPath.ts";
import { createModuleRoutePaths } from "src/system/createModuleRoutePaths.ts";
import { useVoucherSerieListContextMenu } from "src/framework/listV2/context-menu/useVoucherSerieListContextMenu.tsx";
import { useOwnerListContextMenu } from "src/framework/listV2/context-menu/useOwnerListContextMenu.tsx";
import type { ProductRowResource } from "src/framework/listV2/types.ts";
import type { ProductType } from "@exonn/js-sdk";
import { useCreateOne } from "src/system/useCreateOne.ts";
import { ProductCreateFormSearchParams } from "src/admin/modules/product/constants.ts";
import { useModuleEditDisabled } from "src/system/useModuleEditDisabled.ts";
import { useModuleCreateDisabled } from "src/system/useModuleCreateDisabled.ts";
import { useModuleDeleteDisabled } from "src/system/useModuleDeleteDisabled.ts";
import { useModule } from "src/system/useModule.tsx";
import { useIsMutating } from "@tanstack/react-query";
import { useBankSepaListContextMenu } from "src/framework/listV2/context-menu/useBankSepaListContextMenu.tsx";
import { useErpTranslation } from "src/system/useErpTranslation.ts";

export type ModuleListV2Options = {
  resource: string;
};

export function ModuleListV2<Row extends GridValidRowModel = GridValidRowModel>(
  props: ModuleListV2Options,
) {
  const { resource } = props;
  const gridRef = useGridApiRef();

  const { isPending: isCreatingB2bGateOrder, mutate: createB2bGateOrder } =
    useCreateOne({
      resource: ResourceEnum.B2bGateOrder,
    });

  const isAutomaticallyBookingBankTransactions =
    useIsMutating({
      mutationKey: ["automaticallyBookBankTransactions"],
    }) > 0;

  const isCreatingSepaFiles =
    useIsMutating({
      mutationKey: ["bank-sepa", "create-files"],
    }) > 0;

  const clientConfig = useClientConfig();
  const {
    value: selectProductTypeModalOpen,
    setFalse: closeSelectProductTypeModal,
    setTrue: openSelectProductModal,
  } = useBoolean(false);

  const { t } = useErpTranslation({
    resource,
    type: "list",
  });

  const moduleTabsStore = useModuleTabs();

  const { closeEditTab } = moduleTabsStore;

  const useAdditionalContextMenu = getAdditionalContextMenuHook({
    resource,
  });

  const disableEdit = useModuleEditDisabled(resource);
  const disableCreate = useModuleCreateDisabled(resource);
  const disableDelete = useModuleDeleteDisabled(resource);

  const { list: listConfig } = useModule(resource);

  const { hideDeleteAction, hideEditAction } = listConfig || {};

  const { router } = useDataRouterContext();

  const [selectedRow, setSelectedRow] = useState<Row>();

  const { mutate: deleteOne, isPending: isDeleting } = useDeleteOne({
    resource: resource,
  });

  const closeContextMenu = () => {
    setSelectedRow(undefined);
  };

  const deleteAction = {
    icon: <i className={"icon-15-delete"} />,
    key: "delete",

    ConfirmComponent: DeleteRecordConfirmation,
    danger: true,
    label: t("actions.delete", {
      ns: TranslationNamespaceEnum.Common,
    }),
    hide: (row: Row) => {
      if (disableDelete) {
        return true;
      }
      if (hideDeleteAction) {
        return hideDeleteAction(row);
      }

      return false;
    },
    onSelect: () => {},
    onConfirm: (row: Row) => {
      const id = getResourceId({
        row,
        resource,
      });

      deleteOne(
        {
          id,
        },
        {
          onSuccess: () => {
            closeEditTab({
              id: id,
            });
          },
          onSettled: () => {
            closeContextMenu();
          },
        },
      );
    },
  };

  const editAction = {
    key: "edit",
    label: t("actions.edit", {
      ns: TranslationNamespaceEnum.Common,
    }),
    hide: (row: Row) => {
      if (disableEdit) {
        return true;
      }
      if (hideEditAction) {
        return hideEditAction(row);
      }
      return false;
    },
    icon: <i className={"icon-15-edit"} />,

    onSelect: (row: Row) => {
      router.navigate(
        getEditResourcePath({
          resource,
          row,
        }),
      );
    },
  };

  const {
    items: additionalContextMenu,
    isPending: isAdditionalContextMenuItemsPending,
  } = useAdditionalContextMenu({
    selectedRow: selectedRow,
    onClose: closeContextMenu,
  });

  const contextMenu: DataGridContextMenuProps<Row> = {
    // isLoading: isContextMutationPending,
    selectedRow: selectedRow,
    onSelectedRowChange: setSelectedRow,
    items: selectedRow
      ? [editAction, ...additionalContextMenu, deleteAction]
      : [],
  };

  const handleRowDoubleClick = (params: GridRowParams) => {
    if (disableEdit) {
      return;
    }
    if (hideEditAction && hideEditAction(params.row)) {
      return;
    }
    router.navigate(
      getEditResourcePath({
        row: params.row,
        resource,
      }),
    );
  };

  const handleCreateNewRecordClick = () => {
    // this code look very fragile. I wish we had tests for this
    // to many exceptions

    if (resource === ResourceEnum.B2bGateOrder) {
      createB2bGateOrder(
        {
          locale: "de",
          data: {},
        },
        {
          onSuccess: (data) => {
            const routePaths = createModuleRoutePaths({
              resource: ResourceEnum.B2bGateOrder,
            });
            router.navigate(
              replacePathParams(routePaths.edit.root, {
                id: data.id!,
              }),
            );
          },
        },
      );
      return;
    }
    if (clientConfig.appVariation === AppVariation.B2bGateSupplier) {
      // in supplier b2b we don't need to open product type modal
      // because we only support one product type
      moduleTabsStore.openCreateTab();
      return;
    }

    if (resource === ResourceEnum.Product) {
      if (moduleTabsStore.isCreateTabOpen) {
        moduleTabsStore.openCreateTab();
      } else {
        openSelectProductModal();
      }
      return;
    }
    moduleTabsStore.openCreateTab();
  };

  const handleSelectProductType = (type: ProductType) => {
    const searchParams = new URLSearchParams();
    searchParams.set(ProductCreateFormSearchParams.InitialProductType, type);
    router.navigate({
      pathname: moduleTabsStore.createPath,
      search: searchParams.toString(),
    });
  };

  const loading =
    isDeleting ||
    isAdditionalContextMenuItemsPending ||
    (isCreatingB2bGateOrder && resource === ResourceEnum.B2bGateOrder) ||
    (isCreatingSepaFiles && resource === ResourceEnum.BankSepa) ||
    (isAutomaticallyBookingBankTransactions &&
      resource === ResourceEnum.BankTransaction);

  return (
    // todo: loading state
    <Suspense fallback={""}>
      {selectProductTypeModalOpen && resource === ResourceEnum.Product && (
        <SelectProductType
          onClose={closeSelectProductTypeModal}
          onSelect={handleSelectProductType}
        />
      )}
      <ResourceList
        apiRef={gridRef}
        resource={resource}
        loading={loading}
        onToolbarCreateNewRecord={handleCreateNewRecordClick}
        onRowDoubleClick={handleRowDoubleClick}
        contextMenu={contextMenu}
        hideToolbarCreateNewRecordButton={disableCreate}
        style={{
          padding: "15px",
        }}
      />
    </Suspense>
  );
}

type ContextMenuHook<Resource extends GridValidRowModel = GridValidRowModel> =
  (params: { selectedRow?: Resource; onClose: () => void }) => {
    items: DataGridContextMenuProps<Resource>["items"];
    isPending: boolean;
  };

const getAdditionalContextMenuHook = ({
  resource,
}: {
  resource: string;
}): ContextMenuHook<any> => {
  // if (resource === ResourceEnum.Country) {
  //   return useCountryListContextMenu;
  // }
  if (resource === ResourceEnum.VoucherSerie) {
    return useVoucherSerieListContextMenu;
  }
  if (resource === ResourceEnum.Owner) {
    return useOwnerListContextMenu;
  }
  if (resource === ResourceEnum.BankSepa) {
    return useBankSepaListContextMenu;
  }
  return useNoopAdditionalContextMenu;
};

const useNoopAdditionalContextMenu: ContextMenuHook = () => {
  return {
    items: [],
    isPending: false,
  };
};

const getEditResourcePath = ({
  resource,
  row,
}: {
  resource: string;
  row: GridValidRowModel;
}) => {
  if (resource === ResourceEnum.Product) {
    return getProductRowPath({
      row: row as ProductRowResource,
    });
  }
  const paths = createModuleRoutePaths({
    resource,
  });
  return replacePathParams(paths.edit.root, {
    id: row.id,
  });
};

const getResourceId = ({
  row,
}: {
  resource: string;
  row: GridValidRowModel;
}) => {
  return row.id;
};
